// BrainDashboardLayout — the immersive Executive Brain shell (faithful port of the
// approved artifact's frame): scoped `.eb-surface` grid, dark sidebar (brand · nav ·
// status · user · ← Cipher), and a `.eb-main` content column that hosts each screen.
// Immersive (not wrapped in Cipher's DashboardLayout); Cipher stays reachable via
// "← Cipher". The scoped stylesheet (./brain.css) only matches inside `.eb-surface`,
// so it is inert everywhere else and when the feature flag is OFF.
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./brain.css";
import { MOCK_HEADER } from "./mock/mockData";

const NAV = [
  { path: "/brain", label: "Brain", end: true, icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M12 4c3 0 4.5 1.7 4.5 3.6 1.3.4 1.8 1.5 1.8 2.6 0 .9-.5 1.8-1.3 2.2.2 2-1.4 3.6-3.5 3.6M12 4c-3 0-4.5 1.7-4.5 3.6-1.3.4-1.8 1.5-1.8 2.6 0 .9.5 1.8 1.3 2.2-.2 2 1.4 3.6 3.5 3.6M12 4v14"/></svg>
  ) },
  { path: "/brain/decisions", label: "Decisions", icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="3" y="4" width="7" height="7" rx="1.5"/><rect x="14" y="4" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>
  ) },
  { path: "/brain/memory", label: "Memory", icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M8 9h8M8 13h5"/></svg>
  ) },
  { path: "/brain/operations", label: "Operations", icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="12" cy="12" r="3.2"/><path d="M12 4v2M12 18v2M4 12h2M18 12h2M6.3 6.3l1.4 1.4M16.3 16.3l1.4 1.4M17.7 6.3l-1.4 1.4M7.7 16.3l-1.4 1.4"/></svg>
  ) },
  { path: "/brain/living", label: "Living Canvas", icon: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="12" cy="12" r="2"/><circle cx="5" cy="6" r="1.6"/><circle cx="19" cy="6" r="1.6"/><circle cx="5" cy="18" r="1.6"/><circle cx="19" cy="18" r="1.6"/><path d="M10.4 10.8L6.2 7M13.6 10.8L17.8 7M10.4 13.2L6.2 17M13.6 13.2L17.8 17"/></svg>
  ) },
];

function active(pathname, item) {
  if (item.end) return pathname === item.path || pathname.startsWith("/brain/decisions/");
  return pathname === item.path || pathname.startsWith(item.path + "/");
}

export default function BrainDashboardLayout({ children, user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const initials = ((user && (user.name || user.operator_code)) || "?").slice(0, 2).toUpperCase();

  return (
    <div className="eb-surface" data-testid="brain-surface-root">
      <aside className="sidebar" aria-label="Executive Brain navigation">
        <div className="brand">
          <svg className="mark" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M12 3c3.5 0 5 2 5 4.2 1.5.5 2 1.8 2 3.1 0 1-.5 2-1.4 2.5.2 2.4-1.7 4.2-4.1 4.2H8.6C6.2 17 4.3 15.2 4.5 12.8 3.6 12.3 3 11.3 3 10.3c0-1.3.5-2.6 2-3.1C5 5 6.5 3 12 3Z"/><path d="M12 7v10M9 10h6M9 14h6"/></svg>
          <b>Executive Brain</b>
        </div>
        {/* Always-visible return to the main Cipher dashboard */}
        <nav className="nav" aria-label="Return to Cipher dashboard">
          <a data-testid="brain-back-dashboard" onClick={() => navigate("/dashboard")}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M15 6l-6 6 6 6"/></svg>
            Dashboard
          </a>
        </nav>
        <nav className="nav" aria-label="Brain sections">
          {NAV.map((item) => {
            const on = active(location.pathname, item);
            return (
              <a key={item.path} className={on ? "active" : ""} aria-current={on ? "page" : undefined}
                data-testid={`brain-nav-${item.label.toLowerCase()}`} onClick={() => navigate(item.path)}>
                {item.icon}{item.label}
              </a>
            );
          })}
        </nav>
        <div className="side-foot">
          <div className="stat"><div className="lbl">System Status</div><div className="row"><span className="dot live" />{MOCK_HEADER.systemStatus}</div></div>
          <div className="stat"><div className="lbl">Data Pipeline</div>
            <svg className="wavemini" viewBox="0 0 200 24" preserveAspectRatio="none" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M0 15 L14 14 22 8 30 17 42 11 52 13 62 5 72 16 84 10 96 14 108 7 120 15 132 11 144 13 156 6 168 15 180 11 192 14 200 12"/></svg>
            <div className="row" style={{ fontSize: "11px", color: "var(--dim)" }}><span className="dot live" style={{ width: "6px", height: "6px" }} />{MOCK_HEADER.pipeline}</div>
          </div>
          <div className="user"><div className="ava">{initials}</div>
            <div><b>{(user && user.name) || "—"}</b><span>{(user && user.role) || ""}</span></div></div>
          <button className="signout" data-testid="back-to-cipher" onClick={() => navigate("/dashboard")}>← Cipher</button>
        </div>
      </aside>
      <main className="eb-main">{children}</main>
    </div>
  );
}
